/*
 * EVASHU HOMEPAGE MEDIA GUARD — capture-based failure owner, 2026-10-08
 * Uses one capture listener instead of scanning every homepage image and
 * attaching one listener per image. One idle pass catches failures that may
 * have completed before this deferred runtime executed.
 */
(() => {
  'use strict';

  const fail = (image) => {
    if (!(image instanceof HTMLImageElement) || !image.closest('[data-eih-home]')) return;
    const media = image.closest(
      '.eih-hero__image, .eih-product-card__media, .eih-standard__media, .eih-edu__media'
    );
    if (media) media.classList.add('eih-media-failed');
    image.setAttribute('aria-hidden', 'true');
  };

  document.addEventListener('error', (event) => fail(event.target), true);

  const sweepCompletedFailures = () => {
    document.querySelectorAll('[data-eih-home] img').forEach((image) => {
      if (image.complete && image.naturalWidth === 0) fail(image);
    });
  };

  const scheduleSweep = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(sweepCompletedFailures, { timeout: 1200 });
    } else {
      setTimeout(sweepCompletedFailures, 0);
    }
  };

  if (document.readyState === 'complete') scheduleSweep();
  else window.addEventListener('load', scheduleSweep, { once: true });
})();


/*
 * EVASHU HOMEPAGE RUNTIME COORDINATOR — COLLECTION TABS — 2026-09-29
 * The active eight-card collection is server-rendered before first paint.
 * Inactive panels hydrate once through Shopify's Section Rendering API on
 * shopper intent, then stay cached in the DOM. No initial hidden 40-card tree.
 */
(() => {
  'use strict';

  const announce = (home, message) => {
    const node = home.querySelector('.eih-runtime-status');
    if (!node) return;
    node.textContent = '';
    requestAnimationFrame(() => { node.textContent = message; });
  };

  const fragmentUrl = (handle) => {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const url = new URL(`${normalizedRoot}collections/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('section_id', 'evashu-home-product-panel');
    const previewThemeId = new URLSearchParams(location.search).get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    return url.toString();
  };

  const hydratePanel = (panel) => {
    if (panel.dataset.eihHydrated === 'true') return Promise.resolve(panel);
    if (panel._eihHydrationPromise) return panel._eihHydrationPromise;

    const handle = panel.dataset.eihCollectionPanel;
    if (!handle) return Promise.reject(new Error('Missing collection handle'));

    panel.setAttribute('aria-busy', 'true');
    panel._eihHydrationPromise = fetch(fragmentUrl(handle), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('[data-eih-panel-fragment]');
        if (!fragment) throw new Error('Missing product panel fragment');

        const nodes = Array.from(fragment.childNodes, (node) => document.importNode(node, true));
        panel.replaceChildren(...nodes);
        panel.dataset.eihHydrated = 'true';
        panel.removeAttribute('aria-busy');
        panel.dispatchEvent(new CustomEvent('eih:panel:hydrated', { bubbles: true }));
        return panel;
      })
      .catch((error) => {
        panel.removeAttribute('aria-busy');
        panel._eihHydrationPromise = null;
        throw error;
      });

    return panel._eihHydrationPromise;
  };

  const bindCollectionTabs = (home) => {
    if (home.dataset.eihCollectionTabsReady === 'true') return;

    const tabs = Array.from(home.querySelectorAll('[data-eih-collection-tab]'));
    const panels = Array.from(home.querySelectorAll('[data-eih-collection-panel]'));
    if (!tabs.length || !panels.length) return;

    home.dataset.eihCollectionTabsReady = 'true';
    let activationToken = 0;

    const panelForTab = (tab) => {
      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      return panel && home.contains(panel) ? panel : null;
    };

    const warm = (tab) => {
      const panel = panelForTab(tab);
      if (!panel || panel.dataset.eihHydrated === 'true') return;
      hydratePanel(panel).catch(() => {});
    };

    const show = (tab, panel) => {
      tabs.forEach((candidate) => {
        const active = candidate === tab;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-selected', String(active));
        candidate.setAttribute('aria-pressed', String(active));
        candidate.tabIndex = active ? 0 : -1;
      });

      panels.forEach((candidate) => {
        const active = candidate === panel;
        candidate.hidden = !active;
        candidate.classList.toggle('is-active', active);
      });
    };

    const activate = async (tab, focus = false) => {
      const panel = panelForTab(tab);
      if (!panel) return;

      if (focus) tab.focus({ preventScroll: true });
      const token = ++activationToken;

      if (panel.dataset.eihHydrated !== 'true') {
        announce(home, `Loading ${tab.textContent.trim()} products.`);
      }

      try {
        await hydratePanel(panel);
      } catch (_error) {
        if (token === activationToken) announce(home, 'Products could not be loaded. Please try again.');
        return;
      }

      if (token !== activationToken) return;
      show(tab, panel);
      announce(home, `${tab.textContent.trim()}: ${panel.querySelectorAll('.eih-product-card').length} products shown.`);
    };

    tabs.forEach((tab, index) => {
      const selected = tab.getAttribute('aria-selected') === 'true';
      tab.setAttribute('aria-pressed', String(selected));
      tab.tabIndex = selected ? 0 : -1;

      tab.addEventListener('pointerenter', () => warm(tab), { passive: true });
      tab.addEventListener('pointerdown', () => warm(tab), { passive: true });
      tab.addEventListener('focus', () => warm(tab));
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        let targetIndex = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = tabs.length - 1;
        if (targetIndex === null) return;
        event.preventDefault();
        activate(tabs[targetIndex], true);
      });
    });
  };

  const initHome = (home) => {
    if (!(home instanceof Element)) return;
    bindCollectionTabs(home);
  };

  const start = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-eih-home]')) initHome(scope);
    scope.querySelectorAll?.('[data-eih-home]').forEach(initHome);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start(document), { once: true });
  } else {
    start(document);
  }

  document.addEventListener('shopify:section:load', (event) => start(event.target));
})();
